import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';

import {
	Button,
	Confirm,
	GridContainer,
	Icons,
	Input,
	InputType,
	Modal,
	Select,
	SpinnerOverlay,
	validationErrMessages,
} from '@components/common';
import RadioButton from '@components/common/Form/RadioButton';

import { useManageCellOptions } from '@hooks/useManageCellOptions';

import { ConverterLTE, TFrequencyConverterResult } from '@utils/frequencyConverters/earfcnConverter';
import { ConverterT5G } from '@utils/frequencyConverters/nrarfcnConverter';
import { removeSpecialCharacters } from '@utils/removeSpecialCharacters';

import { ICell4GDTO, ICell5GDTO } from '@interfaces/cells/dto/cellsDTO';
import { TRange } from '@interfaces/common/range';
import { TSelectOption } from '@interfaces/components/componentTypes';

import { EVendorsValues, TVendorsValues } from '@enums/common';
import { ERevalidationMode, EValidationMode } from '@enums/validationMode';

import { MODAL_WIDTH_480, modalCloseConfirm } from '@constants/modals';
import { regExpDecimal, regExpDecimalOnlyThousandths } from '@constants/regExp';
import { fiveGFormAreas, formFields, fourGFormAreas, mechanicalTiltBracketOptions } from './constants';

import { schema4g, schema5g } from './schema';

import styles from './CellForm.module.scss';

export interface ICellFormProps {
	editableCell: Record<string, any> | null;
	is4GFdd: boolean;
	is4GTdd: boolean;
	is5G: boolean;
	isLoadingForm: boolean;
	setIsDirtyFormModal: React.Dispatch<React.SetStateAction<boolean>>;
	submitHandler: ({ values, cellId }: { values: ICell5GDTO | ICell4GDTO; cellId: number }) => void;
	toggleCellModal: () => void;
	vendor?: TVendorsValues;
}

export interface IBandFrequency<T = TRange<string>> {
	frequencyRange: T;
}

type TBandSelectOption = TSelectOption & IBandFrequency;
type TBandType = 'FDD' | 'TDD' | '5G' | undefined;

const CellForm = ({
	editableCell,
	is4GFdd,
	is4GTdd,
	is5G,
	isLoadingForm,
	setIsDirtyFormModal,
	submitHandler,
	toggleCellModal,
	vendor,
}: ICellFormProps) => {
	const [isConfirmModal, setIsConfirmModal] = useState(false);

	const [calculatedBandType, setCalculatedBandType] = useState<TBandType>();

	const {
		antennaModelsOptions,
		band4GFddOptions,
		band4GTddOptions,
		band5GOptions,
		isLoadingTechnologiesOptions,
		zoneOptions,
	} = useManageCellOptions();

	const bandOptions = useMemo(() => is5G ? band5GOptions : is4GFdd ? band4GFddOptions : band4GTddOptions,
		[band4GFddOptions, band4GTddOptions, band5GOptions, is4GFdd, is5G]);

	const [filteredBandOptions, setFilteredBandOptions] = useState<TSelectOption[]>([]);
	const [activeFieldName, setActiveFieldName] = useState<string>('');
	const [currentBand, setCurrentBand] = useState<string | null>('');

	const {
		clearErrors,
		control,
		formState: { errors, isDirty },
		getValues,
		handleSubmit,
		register,
		setError,
		setValue,
		watch,
	} = useForm<FieldValues>({
		mode: EValidationMode.all,
		reValidateMode: ERevalidationMode.onChange,
		resolver: yupResolver(is5G ? schema5g : schema4g),
		defaultValues: {
			antennaModel: null,
			azimuth: editableCell?.azimuth || '',
			band: null,
			cellId: editableCell?.cellId || '',
			earfcn: editableCell?.earfcn || undefined,
			electricalTilt: editableCell?.electricalTilt || '',
			frequency: editableCell?.frequency || undefined,
			height: editableCell?.height || '',
			mechanicalTilt: editableCell?.mechanicalTilt || '',
			mechanicalTiltBracket: editableCell?.mechanicalTiltBracket ?? null,
			mechanicalTiltBracketMount: editableCell?.mechanicalTiltBracketMount || '',
			name: editableCell?.name || '',
			nrarfcn: editableCell?.nrarfcn || '',
			pci: editableCell?.pci || '',
			spinningRange: editableCell?.spinningRange || '',
			tac: editableCell?.tac || '',
			totalTilt: editableCell?.totalTilt || '',
			zone: null,
		},
	});

	useEffect(() => {
		if (!bandOptions.length) return;

		const bandValue = bandOptions?.find((band: TBandSelectOption) => band?.value === editableCell?.[is5G ? 'band5g' : 'band4g']?.name);

		setValue(formFields.band.name, bandValue);
	}, [bandOptions, editableCell, isLoadingTechnologiesOptions, is5G, setValue]);

	useEffect(() => {
		setValue(
			formFields.antennaModel.name,
			antennaModelsOptions?.find((antennaModel: TSelectOption) => (
				antennaModel?.value === editableCell?.antennaModel?.title
			)),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [antennaModelsOptions]);

	useEffect(() => {
		setValue(
			formFields.zone.name,
			zoneOptions?.find((zone: TSelectOption) => zone?.value === editableCell?.zone?.title),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [zoneOptions]);

	useEffect(() => setIsDirtyFormModal(isDirty), [isDirty, setIsDirtyFormModal]);

	const handleCloseModals = () => {
		if (isDirty) setIsConfirmModal(true);
		else toggleCellModal();
	};

	const handleCloseConfirmModal = () => setIsConfirmModal(false);

	const onSubmit = (values: FieldValues) => {
		const band = is5G ? { band5g: values.band.value } : { band4g: values.band.value };
		const commonData = {
			...band,
			antennaModel: values.antennaModel.value || null,
			azimuth: +values.azimuth || null,
			cellId: +values.cellId,
			electricalTilt: +values.electricalTilt || null,
			frequency: +values.frequency || null,
			height: +values.height || null,
			mechanicalTilt: +values.mechanicalTilt || null,
			name: values.name || null,
		};

		const dataByTechnology = {
			...commonData,
			...(is5G
				? {
					mechanicalTiltBracket: values.mechanicalTiltBracket,
					mechanicalTiltBracketMount: +values.mechanicalTiltBracketMount || null,
					nrarfcn: +values.nrarfcn || null,
					spinningRange: values.spinningRange || null,
				}
				: {
					earfcn: +values.earfcn || null,
					pci: +values.pci || null,
					tac: +values.tac || null,
					totalTilt: +values.totalTilt || null,
					zone: values.zone?.value || null,
				}),
		};

		submitHandler({ values: dataByTechnology as ICell5GDTO | ICell4GDTO, cellId: editableCell?.cellId });
	};

	const earfcnValue = watch(formFields.earfcn.name);
	const nrarfcnValue = watch(formFields.nrarfcn.name);
	const frequencyValue = watch(formFields.frequency.name);

	const handleOnFocus = (name?: string) => setActiveFieldName(name || '');

	const handleOnBlur = () => setActiveFieldName('');

	const prevEarfcnValue = useRef();
	const prevNrarfcnValue = useRef();
	const prevFrequencyValue = useRef();
	const isFirstRender = useRef(true);

	const getComputedFrequency = useCallback((
		value: string,
		valueToFrequencyFn: (value: string) => TFrequencyConverterResult,
	): void => {
		if (value === '') return;

		clearErrors(formFields.frequency.name);

		const { frequency, bandType, band } = valueToFrequencyFn(value);

		if (!is5G) {
			setCurrentBand(band ? `B${band}` : null);
		}

		const isInvalidFrequency = isNaN(frequency);

		setCalculatedBandType(bandType as TBandType);

		if (isInvalidFrequency) {
			setValue(formFields.frequency.name, null);

			return;
		}

		setValue(formFields.frequency.name, frequency);
	}, [clearErrors, is5G, setValue]);

	const fieldNameMap = useMemo(() => {
		return {
			[formFields.earfcn.name]: {
				prevValue: prevEarfcnValue,
				value: earfcnValue,
				valueToFrequencyFn: ConverterLTE.earfcnToFreq,
			},
			[formFields.nrarfcn.name]: {
				prevValue: prevNrarfcnValue,
				value: nrarfcnValue,
				valueToFrequencyFn: ConverterT5G.nrarfcnToFreq,
			},
		};
	}, [earfcnValue, nrarfcnValue]);

	useEffect(() => {
		const isEarfcnActive = (activeFieldName === formFields.earfcn.name && !is5G);
		const isNrarfcnActive = (activeFieldName === formFields.nrarfcn.name && is5G);

		if (isEarfcnActive || isNrarfcnActive) {
			if (
				fieldNameMap[activeFieldName].value !== fieldNameMap[activeFieldName].prevValue.current &&
				fieldNameMap[activeFieldName].prevValue.current
			) {
				setValue(formFields.band.name, null);
			}

			if (fieldNameMap[activeFieldName].value === '') {
				setValue(formFields.frequency.name, null);
			}

			getComputedFrequency(
				fieldNameMap[activeFieldName].value,
				(fieldNameMap[activeFieldName] as any).valueToFrequencyFn,
			);

			fieldNameMap[activeFieldName].prevValue.current = fieldNameMap[activeFieldName].value;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		clearErrors,
		earfcnValue,
		fieldNameMap,
		getComputedFrequency,
		is5G,
		nrarfcnValue,
		setError,
		setValue,
	]);

	useEffect(() => {
		if (calculatedBandType && calculatedBandType === 'TDD' && is4GFdd) {
			setError(formFields.earfcn.name, {
				message: 'This value is not supported for 4G FDD',
				type: 'custom',
			});
		}

		if (calculatedBandType && calculatedBandType === 'FDD' && is4GTdd) {
			setError(formFields.earfcn.name, {
				message: 'This value is not supported for 4G TDD',
				type: 'custom',
			});
		}
	}, [frequencyValue, calculatedBandType, is4GFdd, is4GTdd, setError]);

	useEffect(() => {
		if (activeFieldName === formFields.frequency.name && is5G) {
			if (frequencyValue === '') return;

			clearErrors(formFields.nrarfcn.name);

			const nrarfcn = ConverterT5G.freqToNrarfcn(frequencyValue);
			const isInvalidNrarfcn = isNaN(nrarfcn);

			if (isInvalidNrarfcn) {
				setError(formFields.frequency.name, { message: validationErrMessages.frequencyInvalid });
				setValue(formFields.nrarfcn.name, null);

				return;
			}

			setValue(formFields.nrarfcn.name, nrarfcn);
		}
	}, [frequencyValue, activeFieldName, is5G, setValue, setError, clearErrors]);

	useEffect(() => {
		if (!bandOptions.length) return;

		const filteredBandOptions: TSelectOption[] = bandOptions
			?.filter((band: TBandSelectOption) => (
				+frequencyValue >= +band.frequencyRange.from && +frequencyValue <= +band.frequencyRange.to
			)).map((band: TBandSelectOption) => {
				const { frequencyRange, ...rest } = band;

				return rest;
			});

		const options = frequencyValue
			? (is5G
					? filteredBandOptions
					: filteredBandOptions.filter(band => band?.name === currentBand)
			)
			: bandOptions;

		setFilteredBandOptions(options);

		if (frequencyValue === prevFrequencyValue.current || isFirstRender.current) return;

		isFirstRender.current = false;
		prevFrequencyValue.current = frequencyValue;

		setValue(formFields.band.name, null);
	}, [bandOptions, currentBand, frequencyValue, is5G, setValue]);

	const isDisableCommonFieldsForEditLimenet = vendor === EVendorsValues.limenet && !!editableCell?.cellId;
	const isBandDisabled = isDisableCommonFieldsForEditLimenet
		|| (is5G ? !nrarfcnValue : !earfcnValue)
		|| !frequencyValue;

	const onFrequencyChange = (value: string) => {
		const formattedValue = !regExpDecimalOnlyThousandths.test(value) ? value.slice(0, -1) : value;

		removeSpecialCharacters(formattedValue, formFields.frequency.name, setValue, regExpDecimal);
	};

	return (
		<GridContainer
			customClassName={styles.cellFormContainer}
			gap='15px 20px'
			gridTemplateAreas={is5G ? fiveGFormAreas : fourGFormAreas}
			template='repeat(2, 240px)'
		>
			{isLoadingForm && <SpinnerOverlay />}
			<Modal
				isOpen={isConfirmModal}
				onClose={handleCloseConfirmModal}
				width={MODAL_WIDTH_480}
			>
				<Confirm
					onClose={handleCloseConfirmModal}
					onSubmit={toggleCellModal}
					question={modalCloseConfirm.question}
				>
					{modalCloseConfirm.children}
				</Confirm>
			</Modal>
			<div className={styles.cellId}>
				<Input
					disabled={isDisableCommonFieldsForEditLimenet}
					errorMessage={errors?.cellId?.message}
					label={formFields.cellId.label}
					name={formFields.cellId.name}
					placeholder={formFields.cellId.placeholder}
					register={register}
					required
					setValue={setValue}
					type={InputType.integer}
				/>
			</div>
			<div className={styles.name}>
				<Input
					disabled={isDisableCommonFieldsForEditLimenet}
					errorMessage={errors?.name?.message}
					label={formFields.name.label}
					name={formFields.name.name}
					placeholder={formFields.name.placeholder}
					register={register}
				/>
			</div>
			<div className={styles.frequency}>
				<Input
					disabled={isDisableCommonFieldsForEditLimenet}
					errorMessage={errors?.frequency?.message}
					isReadOnly={!is5G}
					label={formFields.frequency.label}
					name={formFields.frequency.name}
					onBlur={handleOnBlur}
					onChange={onFrequencyChange}
					onFocus={handleOnFocus}
					placeholder={formFields.frequency.placeholder}
					register={register}
					required
					type={InputType.float}
				/>
				<div className={styles.interlinkIcon}>
					{Icons.interlink()}
				</div>
			</div>
			<div className={styles.band}>
				<Select
					control={control}
					disabled={isBandDisabled}
					errorMessage={errors.band?.message}
					isError={!!errors?.band}
					label={formFields.band.label}
					name={formFields.band.name}
					noOptionsMessage='No available band'
					options={filteredBandOptions}
					placeholder={formFields.band.placeholder}
					required
					testId='band-select'
				/>
			</div>
			<div className={styles.antennaModel}>
				<Select
					control={control}
					errorMessage={errors.antennaModel?.message || (errors.antennaModel as any)?.value?.message}
					isClearable
					isCreatable
					isError={!!errors?.antennaModel}
					label={formFields.antennaModel.label}
					name={formFields.antennaModel.name}
					options={antennaModelsOptions}
					placeholder={formFields.antennaModel.placeholder}
					required
				/>
			</div>
			<div className={styles.height}>
				<Input
					errorMessage={errors?.height?.message}
					label={formFields.height.label}
					name={formFields.height.name}
					placeholder={formFields.height.placeholder}
					register={register}
					setValue={setValue}
					type={InputType.integer}
				/>
			</div>
			<div className={styles.azimuth}>
				<Input
					errorMessage={errors?.azimuth?.message}
					label={formFields.azimuth.label}
					name={formFields.azimuth.name}
					placeholder={formFields.azimuth.placeholder}
					register={register}
					setValue={setValue}
					type={InputType.integer}
				/>
			</div>
			<div className={styles.mechanicalTilt}>
				<Input
					errorMessage={errors?.mechanicalTilt?.message}
					label={formFields.mechanicalTilt.label}
					name={formFields.mechanicalTilt.name}
					placeholder={formFields.mechanicalTilt.placeholder}
					register={register}
					setValue={setValue}
					type={InputType.integer}
				/>
			</div>
			<div className={styles.electricalTilt}>
				<Input
					errorMessage={errors?.electricalTilt?.message}
					label={formFields.electricalTilt.label}
					name={formFields.electricalTilt.name}
					placeholder={formFields.electricalTilt.placeholder}
					register={register}
					setValue={setValue}
					type={InputType.integer}
				/>
			</div>
			{is5G
				? <>
					<div className={styles.mechanicalTiltBracket}>
						<div className={styles.label}>
							Mechanical Tilt Bracket
						</div>
						<div className={styles.checkboxes}>
							{
								mechanicalTiltBracketOptions.map(({ label, value }) => {
									const onChange = () => setValue(formFields.mechanicalTiltBracket.name, value);

									return (
										<RadioButton
											key={label}
											defaultChecked={getValues(formFields.mechanicalTiltBracket.name) === value}
											id={label}
											label={label}
											name={formFields.mechanicalTiltBracket.name}
											onChange={onChange}
										/>
									);
								})
							}
						</div>
					</div>
					<div className={styles.mechanicalTiltBracketMount}>
						<Input
							errorMessage={errors?.mechanicalTiltBracketMount?.message}
							label={formFields.mechanicalTiltBracketMount.label}
							name={formFields.mechanicalTiltBracketMount.name}
							placeholder={formFields.mechanicalTiltBracketMount.placeholder}
							register={register}
							setValue={setValue}
							type={InputType.integer}
						/>
					</div>
					<div className={styles.spinningRange}>
						<Input
							errorMessage={errors?.spinningRange?.message}
							label={formFields.spinningRange.label}
							name={formFields.spinningRange.name}
							placeholder={formFields.spinningRange.placeholder}
							register={register}
						/>
					</div>
					<div className={styles.nrarfcn}>
						<Input
							disabled={isDisableCommonFieldsForEditLimenet}
							errorMessage={errors?.nrarfcn?.message}
							label={formFields.nrarfcn.label}
							name={formFields.nrarfcn.name}
							onBlur={handleOnBlur}
							onFocus={handleOnFocus}
							placeholder={formFields.nrarfcn.placeholder}
							register={register}
							required
							setValue={setValue}
							type={InputType.integer}
						/>
					</div>
				</>
				: <>
					<div className={styles.totalTilt}>
						<Input
							errorMessage={errors?.totalTilt?.message}
							label={formFields.totalTilt.label}
							name={formFields.totalTilt.name}
							placeholder={formFields.totalTilt.placeholder}
							register={register}
							setValue={setValue}
							type={InputType.integer}
						/>
					</div>
					<div className={styles.zone}>
						<Select
							control={control}
							errorMessage={errors.zone?.message || (errors.zone as any)?.value?.message}
							isClearable
							isCreatable
							isError={!!errors?.zone}
							label={formFields.zone.label}
							name={formFields.zone.name}
							options={zoneOptions}
							placeholder={formFields.zone.placeholder}
						/>
					</div>
					<div className={styles.tac}>
						<Input
							errorMessage={errors?.tac?.message}
							isReadOnly={!!editableCell?.tac}
							label={formFields.tac.label}
							name={formFields.tac.name}
							placeholder={formFields.tac.placeholder}
							register={register}
							setValue={setValue}
							type={InputType.integer}
						/>
					</div>
					<div className={styles.earfcn}>
						<Input
							errorMessage={errors?.earfcn?.message}
							label={formFields.earfcn.label}
							name={formFields.earfcn.name}
							onBlur={handleOnBlur}
							onFocus={handleOnFocus}
							placeholder={formFields.earfcn.placeholder}
							register={register}
							required
							setValue={setValue}
							type={InputType.integer}
						/>
					</div>
					<div className={styles.pci}>
						<Input
							errorMessage={errors?.pci?.message}
							label={formFields.pci.label}
							name={formFields.pci.name}
							placeholder={formFields.pci.placeholder}
							register={register}
							setValue={setValue}
							type={InputType.integer}
						/>
					</div>
				</>
			}
			<div className={styles.submitButtons}>
				<Button
					onClick={handleCloseModals}
					text='Cancel'
					withoutBackground
				/>
				<Button
					onClick={handleSubmit(onSubmit)}
					text={editableCell ? 'Save' : 'Add'}
				/>
			</div>
		</GridContainer>
	);
};

export default memo(CellForm);
